import React, { useCallback, useEffect, useState } from 'react';
import './EditGroup.scss';
import { GroupProps } from 'business/models/CustomModels';
import { GroupItem, NewGroupItem } from 'components/customComponents/Layers/InputGroups';
import {
  LayerDTO,
  RandomLayerDTO,
  RandomLayerGenerationListDTO,
  RandomLayerIngredientGroupDTO,
} from 'business/models/Models';
import { getNewId, isNullOrUndefined } from 'utils/Global';
import ELayersView from 'enums/ELayersView';
import cloneDeep from 'lodash/cloneDeep';
import { usePreserveExperiment, useRMDispatch, useRMSelector } from 'redux/hooks';
import { callGenerateLayer } from 'redux/reducers/experimentReducer';
import {
  updateExperimentLayers,
  updateLayerGroupData,
} from 'redux/reducers/experimentDataReducer';
import {
  selectExperimentLayers,
  selectSelectedLayerId,
} from 'redux/selectors/ExperimentStateSelector';
import { withTabBackButton } from 'components/tab';

function EditGroup(props: GroupProps) {
  const {
    setContentView,
    activeGroupId,
    isEditGroupActive,
    setRandomLyrActiveGroupId,
    handleBackToPreviousScreen,
    setHandleOnTabBackPress,
    setSelectedTab,
    setIsStayOnEditGroup,
  } = props;

  const [originalSelectedGroupId, setOriginalSelectedGroupId] = useState(-1);
  const [selectedGroupId, setSelectedGroupId] = useState(-1);
  const [groups, setGroups] = useState<RandomLayerIngredientGroupDTO[]>([]);
  const [newGroup, setNewGroup] = useState<RandomLayerIngredientGroupDTO>();
  const [refresh, setRefresh] = useState(false);
  const [dirty, setIsDirty] = useState(false);
  const [isNewGroupNameInvalid, setIsNewGroupNameInvalid] = useState(false);
  const [changedNameIsInValid, setChangedNameIsInValid] = useState(false);
  const [groupsRenderer, setGroupsRenderer] = useState([]);
  const dispatch = useRMDispatch();
  const genCanvas = useCallback(() => dispatch(callGenerateLayer()), [dispatch]);
  const callPreserveUndoableState = usePreserveExperiment();
  const _layers = useRMSelector(selectExperimentLayers);
  const _selectedLayerId = useRMSelector(selectSelectedLayerId);
  const _setLayers = useCallback(
    (lyrs) => dispatch(updateExperimentLayers(lyrs)),
    [dispatch],
  );
  useEffect(() => {
    const lyr: RandomLayerDTO = _layers.find((l) => l.id === _selectedLayerId)
      .layerDetail as RandomLayerDTO;

    const g = lyr.randomLayerIngredientGroup.find((g) =>
      g.randomLayerGenerationList.find((rlgl) => rlgl.id === activeGroupId),
    );
    if (isNullOrUndefined(g)) {
      setSelectedGroupId(null);
      setOriginalSelectedGroupId(null);
    } else {
      setSelectedGroupId(g.id);
      setOriginalSelectedGroupId(g.id);
    }
    setGroups(cloneDeep(lyr.randomLayerIngredientGroup));
    setIsDirty(false);
  }, []);

  useEffect(() => {
    setGroupsRenderer(getGroups());
  }, [groups, selectedGroupId, newGroup]);

  useEffect(() => {
    setHandleOnTabBackPress(() =>
      handleBackToPreviousScreen(ELayersView.LAYERDESIGNVIEW),
    );
  }, [setHandleOnTabBackPress]);

  function deleteGroup(id) {
    const updatedGroups = [];
    groups.map((g) => {
      if (g.id !== id) {
        updatedGroups.push(g);
      }
    });
    setGroups(updatedGroups);

    setIsStayOnEditGroup(true);
    setLayerGroupData(updatedGroups);
  }
  function onClickRemoveAddGroup(e) {
    setNewGroup(null);
  }
  function onClickAddGroup(e) {
    const updatedGroups = groups;
    setSelectedGroupId(newGroup.id);
    updatedGroups.push(newGroup);
    setGroups(updatedGroups);
    setNewGroup(null);

    setIsStayOnEditGroup(true);
    setLayerGroupData(updatedGroups);
  }
  function onChangeAddGroupName(e) {
    const newg = new RandomLayerIngredientGroupDTO();
    newg.randomLayerGenerationList = [];
    newg.randomLayerGroupIngredient = [];
    newg.name = e.target.value;
    newg.id = getNewId();
    let inValid = false;
    let groupName = e.target.value;
    const splitedGroupName = groupName.toLocaleLowerCase().split(' ');
    const splitedGroupNameSpaces = splitedGroupName.filter((gn) => gn === '');
    if (splitedGroupNameSpaces.length === splitedGroupName.length - 1) {
      groupName = splitedGroupName[0];
    }
    for (const g of groups) {
      if (g.name.toLocaleLowerCase() === groupName) {
        inValid = true;
        break;
      }
    }
    setIsNewGroupNameInvalid(inValid);
    setNewGroup(newg);
  }
  function emptyGroupNameExist() {
    const groupsWithNoName = groups.filter((g) => g.name === '');
    if (groupsWithNoName.length > 0) {
      return true;
    }
    return false;
  }
  function getGroups() {
    const groupsArr = [];
    groupsArr.push(
      <NewGroupItem
        text={isNullOrUndefined(newGroup) ? '' : newGroup.name}
        onClickRemoveAddGroup={onClickRemoveAddGroup}
        onClickAddGroup={onClickAddGroup}
        onChangeAddGroupName={onChangeAddGroupName}
        isNewGroupNameInvalid={isNewGroupNameInvalid}
      />,
    );
    groups.map((g) => {
      let maxProb: number = null;
      if (g.randomLayerGenerationList.length > 0) {
        g.randomLayerGenerationList.map((rlgl) => {
          if (maxProb === null || maxProb < rlgl.probability) {
            maxProb = rlgl.probability;
          }
        });
      }
      groupsArr.push(
        <GroupItem
          text={g.name}
          probability={maxProb}
          key={g.name + Math.random()}
          onClick={() => {
            if (emptyGroupNameExist() || changedNameIsInValid) {
              return;
            }
            setSelectedGroupId(g.id);
          }}
          isSelected={g.id === selectedGroupId}
          onDelete={() => deleteGroup(g.id)}
        />,
      );
    });
    return groupsArr;
  }
  function getNewRlglId() {
    let id = -1;
    groups.map((g) =>
      g.randomLayerGenerationList.map((rlgl) => {
        if (id < rlgl.id) {
          id = rlgl.id;
        } else if (rlgl.id < 0 && id < -rlgl.id) {
          id = -rlgl.id;
        }
      }),
    );
    return -id - 1;
  }
  function changeGroupProbability(e, id) {
    const g = groups;

    if (isEditGroupActive && originalSelectedGroupId !== selectedGroupId) {
      const originalGroup = g.find((group) => group.id === originalSelectedGroupId);
      if (originalGroup) {
        originalGroup.randomLayerGenerationList = [];
        originalGroup.randomLayerGroupIngredient = [];
      }
    }

    let prob = parseFloat(e.target.value) > 1 ? 1 : parseFloat(e.target.value);
    prob = isNaN(parseFloat(e.target.value)) || parseFloat(e.target.value) < 0 ? 0 : prob;
    if (g.find((g) => g.id === selectedGroupId).randomLayerGenerationList.length > 0) {
      g
        .find((g) => g.id === selectedGroupId)
        .randomLayerGenerationList.find((rlgl) => rlgl.id === id).probability = prob;
    } else {
      const newRlglId = getNewRlglId();
      const rlgl = new RandomLayerGenerationListDTO();
      rlgl.id = newRlglId;
      rlgl.probability = prob;
      rlgl.randomLayerId = _selectedLayerId;
      rlgl.randomLayerIngredientGroupId = selectedGroupId;
      const oldRlgl = g.find((g) => g.id === selectedGroupId);
      oldRlgl.randomLayerGenerationList = [];

      oldRlgl.randomLayerGenerationList.push(rlgl);
      setRandomLyrActiveGroupId(newRlglId);
    }
    setIsDirty(true);
    setGroups(g);
    setRefresh(!refresh);
  }

  function onChangeSelectedGroupName(e) {
    const g = groups.find((g) => g.id === selectedGroupId);
    if (isNullOrUndefined(g)) {
      return null;
    }
    let value = e.target.value;
    const parsedValueBySpace = value.split(' ');
    if (parsedValueBySpace.length >= 1) {
      let wordFound = false;
      for (let i = 1; i < parsedValueBySpace.length; i++) {
        if (parsedValueBySpace[i] != '') {
          wordFound = true;
          break;
        }
      }
      if (!wordFound) {
        value = parsedValueBySpace[0];
      }
    }

    const groupsCopy = [...groups];
    const sameGroupNameWithExtraSpace = groups.filter(
      (group) =>
        group.name.toLowerCase() === value.toLowerCase() && group.id !== selectedGroupId,
    );
    groupsCopy.find((group) => group.id === g.id).name = e.target.value;
    const similarGroups = groupsCopy.filter(
      (group) => group.name.toLowerCase() === value.toLowerCase(),
    );
    if (similarGroups.length > 1 || sameGroupNameWithExtraSpace.length > 0) {
      setChangedNameIsInValid(true);
    } else {
      setChangedNameIsInValid(false);
    }
    setGroups(groupsCopy);

    setIsStayOnEditGroup(true);
    setLayerGroupData(groupsCopy);
  }

  function setLayerGroupData(updatedGroups: RandomLayerIngredientGroupDTO[]) {
    dispatch(updateLayerGroupData({ layerId: _selectedLayerId, groups: updatedGroups }));
  }

  function getSelectedGroup() {
    const g = groups?.find((g) => g.id === selectedGroupId);
    let selectedRlgl = new RandomLayerGenerationListDTO();
    g?.randomLayerGenerationList.map((rlgl) => {
      if (
        rlgl.probability > selectedRlgl.probability ||
        isNullOrUndefined(selectedRlgl.probability)
      ) {
        selectedRlgl = rlgl;
      }
    });
    return (
      <>
        <div
          id="commandpanel-randomlayering-editgroup-selected-container"
          className="w-60 space-evenly border-right flex margin-auto"
        >
          <div id="commandpanel-randomlayering-editgroup-label-group">Group</div>
          <div id="commandpanel-randomlayering-editgroup-input-groupname-container">
            <input
              id="commandpanel-randomlayering-editgroup-input-groupname"
              type="text"
              value={g?.name || ''}
              onChange={onChangeSelectedGroupName}
              className={changedNameIsInValid ? 'invalid-new-name' : null}
              disabled={!g}
            />
          </div>
        </div>
        <div
          id="commandpanel-randomlayering-editgroup-probability-container"
          className="w-40 space-evenly flex margin-auto"
        >
          <div id="commandpanel-randomlayering-editgroup-probability">Probability</div>
          <div id="commandpanel-randomlayering-editgroup-input-probability-container">
            <input
              id="commandpanel-randomlayering-editgroup-input-probability"
              type="number"
              step=".1"
              min="0"
              max="1"
              className="probability-input"
              value={
                isNullOrUndefined(selectedRlgl.probability)
                  ? ''
                  : selectedRlgl.probability
              }
              onChange={(e) => changeGroupProbability(e, selectedRlgl.id)}
              disabled={!g}
            />
          </div>
        </div>
      </>
    );
  }
  function onUpdateClick() {
    setHandleOnTabBackPress(null);
    if (isNullOrUndefined(_selectedLayerId)) {
      setContentView(ELayersView.LAYERDESIGNVIEW);
      return;
    }
    callPreserveUndoableState();
    const groupsWithNoName = groups.filter((g) => g.name === '');
    if (groupsWithNoName.length > 0) {
      return;
    }
    const layers = cloneDeep(_layers) as LayerDTO[];
    const lyr = layers.find((l) => l.id === _selectedLayerId);
    (lyr.layerDetail as RandomLayerDTO).randomLayerIngredientGroup = groups;
    lyr.dirty = true;
    _setLayers(layers);
    genCanvas();
    if (setSelectedTab) {
      setSelectedTab(1);
    }
    setContentView(ELayersView.LAYERDESIGNVIEW);
    setIsStayOnEditGroup(false);
  }
  if (groups.length === 0) {
    return null;
  }
  return (
    <div
      id="commandpanel-randomlayering-editgroup-wrapper"
      className="select-ingredients-tabs"
    >
      <div
        id="commandpanel-randomlayering-editgroup-selectedgroup-operation"
        className="group-operation"
      >
        {getSelectedGroup()}
      </div>
      <div
        id="commandpanel-randomlayering-editgroup-groups-container"
        className="groups-container"
      >
        {groupsRenderer}
        {}
      </div>
      <div
        id="commandpanel-randomlayering-editgroup-actions-container"
        className="group-operation float-right flex"
      >
        <button
          id="commandpanel-randomlayering-editgroup-cancel-button"
          type="button"
          className="btn btn-secondary h-70 margin-group-item cancel-group-changes"
          onClick={() => {
            setIsStayOnEditGroup(false);
            setContentView(ELayersView.LAYERDESIGNVIEW);
            setHandleOnTabBackPress(null);
          }}
        >
          CANCEL
        </button>
        <button
          id="commandpanel-randomlayering-editgroup-submit-button"
          type="button"
          className="btn btn-success h-70 margin-group-item"
          onClick={() => onUpdateClick()}
          disabled={
            !dirty || changedNameIsInValid || emptyGroupNameExist() ? true : false
          }
        >
          {isEditGroupActive ? 'UPDATE' : 'ADD'}
        </button>
      </div>
    </div>
  );
}

export default withTabBackButton(EditGroup);
